import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useBridgeClubAuth } from '../../../contexts/BridgeClubAuthContext';
import { bridgeClubAPI } from '../../../services/bridgeClubAPI';
import { deliveryAPI, ZipTier } from '../../../services/deliveryAPI';
import WholesaleLine from '../../../components/BridgeClub/WholesaleLine';
import './OnboardingFlow.css';

type Step = 'welcome' | 'info' | 'location' | 'delivery' | 'opening' | 'store' | 'email_sent';

interface Store {
  id: number;
  name: string;
  city: string;
  state: string;
  address?: string;
  is_legacy?: boolean;  // True if this is a Customer ID (legacy), false/undefined for RetailLocation ID
}

// Email validation utilities
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const DOMAIN_TYPOS: Record<string, string> = {
  // Gmail typos
  'gnail.com': 'gmail.com',
  'gmai.com': 'gmail.com',
  'gmal.com': 'gmail.com',
  'gmial.com': 'gmail.com',
  'gmaill.com': 'gmail.com',
  'gmali.com': 'gmail.com',
  'gamil.com': 'gmail.com',
  'gemail.com': 'gmail.com',
  'gmail.co': 'gmail.com',
  'gmail.con': 'gmail.com',
  'gmail.cm': 'gmail.com',
  'gmail.om': 'gmail.com',
  'gmaik.com': 'gmail.com',
  'gmailc.om': 'gmail.com',
  'g]mail.com': 'gmail.com',
  // Yahoo typos
  'yaho.com': 'yahoo.com',
  'yahooo.com': 'yahoo.com',
  'yhaoo.com': 'yahoo.com',
  'yahoo.co': 'yahoo.com',
  'yahoo.con': 'yahoo.com',
  // Hotmail typos
  'hotmal.com': 'hotmail.com',
  'hotmai.com': 'hotmail.com',
  'hotamil.com': 'hotmail.com',
  'hotmial.com': 'hotmail.com',
  'hotmail.co': 'hotmail.com',
  'hotmail.con': 'hotmail.com',
  // Outlook typos
  'outloo.com': 'outlook.com',
  'outlok.com': 'outlook.com',
  'outlookl.com': 'outlook.com',
  'outlook.co': 'outlook.com',
  'outlook.con': 'outlook.com',
  // iCloud typos
  'icloud.co': 'icloud.com',
  'icoud.com': 'icloud.com',
  'iclould.com': 'icloud.com',
  // AOL typos
  'aol.co': 'aol.com',
  'aol.con': 'aol.com',
};

interface EmailValidation {
  isValid: boolean;
  error?: string;
  suggestion?: string;
}

const validateEmail = (email: string): EmailValidation => {
  if (!email) {
    return { isValid: false, error: 'Email is required' };
  }

  const trimmed = email.trim().toLowerCase();

  // Basic format check
  if (!EMAIL_REGEX.test(trimmed)) {
    return { isValid: false, error: 'Please enter a valid email address' };
  }

  // Check for domain typos
  const domain = trimmed.split('@')[1];
  if (domain && DOMAIN_TYPOS[domain]) {
    const correctedEmail = trimmed.replace(domain, DOMAIN_TYPOS[domain]);
    return {
      isValid: false,
      suggestion: correctedEmail,
      error: `Did you mean ${correctedEmail}?`,
    };
  }

  return { isValid: true };
};

// The state a US ZIP belongs to, for the prefixes a Red Bridge customer is likely to type.
// Only used to prefill: the member can still change it.
const stateForZip = (zip: string): string | null => {
  if (/^83[2-9]/.test(zip)) return 'ID';
  if (/^97/.test(zip)) return 'OR';
  if (/^9[89]/.test(zip)) return 'WA';
  if (/^59/.test(zip)) return 'MT';
  return null;
};

// A throttled request, in words. DRF answers 429 with "Request was throttled. Expected
// available in 3456 seconds." - true, and not something to put in front of a customer.
const isRateLimited = (err: any): boolean =>
  err?.status === 429 || /throttl/i.test(String(err?.detail || ''));
const BUSY_MESSAGE = 'Lots of people are signing up right now. Please wait a minute, then try again.';

const OnboardingFlow: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register, isAuthenticated } = useBridgeClubAuth();

  // ARRIVING WITH A ZIP (the home-delivery landing page links here with ?zip=83605 once
  // the ZIP check says we deliver). The visitor has already read the story and chosen to
  // sign up, so the "Buy 10 bags" welcome screen is skipped and the ZIP - and its state,
  // so an Idaho ZIP is not saved against the WA default - is filled in.
  const zipParam = (searchParams.get('zip') || '').trim();
  const arrivedWithZip = /^\d{5}$/.test(zipParam);

  const [step, setStep] = useState<Step>(arrivedWithZip ? 'info' : 'welcome');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    phone: '',
    city: '',
    state: (arrivedWithZip && stateForZip(zipParam)) || 'WA',
    zip_code: arrivedWithZip ? zipParam : '',
    partner_loyalty_id: '',
  });

  // Legal consent
  const [termsAccepted, setTermsAccepted] = useState(false);

  const [stores, setStores] = useState<Store[]>([]);
  const [selectedStore, setSelectedStore] = useState<Store | null>(null);

  // Where this ZIP stands with delivery, asked after the location step
  // (delivery/public_views.py - public, because no member exists yet). In the Treasure
  // Valley "which store do you belong to?" is the wrong question: there is no store.
  const [zipTier, setZipTier] = useState<ZipTier | null>(null);
  const [deliveryStreet, setDeliveryStreet] = useState('');
  const [wantsOpeningAlert, setWantsOpeningAlert] = useState(true);

  // Email validation state
  const [emailValidation, setEmailValidation] = useState<EmailValidation>({ isValid: true });
  const [emailTouched, setEmailTouched] = useState(false);

  // Validate email on change
  const handleEmailChange = (newEmail: string) => {
    setFormData({ ...formData, email: newEmail });
    if (emailTouched || newEmail.length > 5) {
      setEmailValidation(validateEmail(newEmail));
    }
  };

  // Accept the suggested email
  const acceptEmailSuggestion = () => {
    if (emailValidation.suggestion) {
      setFormData({ ...formData, email: emailValidation.suggestion });
      setEmailValidation({ isValid: true });
    }
  };

  // Redirect if already authenticated
  React.useEffect(() => {
    if (isAuthenticated) {
      navigate('/bridge-club/dashboard');
    }
  }, [isAuthenticated, navigate]);

  // Track QR code visits - send analytics when user lands on page
  useEffect(() => {
    const storeCode = searchParams.get('store');
    const placement = searchParams.get('placement');

    // Debug: Log what params we have
    console.log('[QR Tracking] Params check:', {
      storeCode,
      placement,
      fullSearch: window.location.search,
      searchParamsString: searchParams.toString(),
    });

    // Only track if this looks like a QR code visit
    if (storeCode || placement) {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
      console.log('[QR Tracking] Sending to:', `${API_BASE_URL}/bridge-club/track-visit/`);

      fetch(`${API_BASE_URL}/bridge-club/track-visit/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          store_code: storeCode || '',
          placement: placement || 'direct',
          portal: 'bridge_club',
          path: '/bridge-club',
        }),
      })
        .then(res => {
          console.log('[QR Tracking] Response:', res.status);
          return res.json();
        })
        .then(data => console.log('[QR Tracking] Result:', data))
        .catch(err => console.error('[QR Tracking] Error:', err));
    } else {
      console.log('[QR Tracking] No store/placement params - not tracking');
    }
  }, [searchParams]);

  // Local signup = the Kettle Falls mill portal (bridgeclubkf.*). These
  // users may skip store selection — the backend defaults their home store
  // to the mill. Non-local (retail-partner) signups must pick a store, so
  // skip is disabled for them (prevents home_location=None orphans).
  const isLocalSignup =
    typeof window !== 'undefined' &&
    window.location.hostname.toLowerCase().includes('bridgeclubkf');

  const loadStores = async () => {
    try {
      const data = await bridgeClubAPI.getStores({
        state: formData.state,
        city: formData.city || undefined,
        zip: formData.zip_code || undefined,
      });
      setStores(data);
    } catch (err) {
      console.error('Failed to load stores:', err);
    }
  };

  // One registration call, three ways in. Store ids are simply omitted for a delivery or
  // opening-area signup: the serializer only requires a home store for local members, so
  // a storeless registration is accepted today - asking a Caldwell customer to choose
  // between nineteen Washington stores was the bug, not a backend constraint.
  const submitRegistration = async (storeIds: Record<string, number> = {}) => {
    setLoading(true);
    try {
      await register({
        email: formData.email,
        first_name: formData.first_name,
        last_name: formData.last_name,
        phone: formData.phone,
        city: formData.city,
        state: formData.state,
        zip_code: formData.zip_code,
        ...storeIds,
        partner_loyalty_id: formData.partner_loyalty_id || undefined,
        terms_accepted: termsAccepted,
        privacy_accepted: termsAccepted, // Same checkbox covers both
        is_local_signup: isLocalSignup,
      });
      return true;
    } catch (err: any) {
      if (isRateLimited(err)) {
        setError(BUSY_MESSAGE);
        return false;
      }
      setError(err?.email?.[0] || err?.detail || err?.terms_accepted?.[0] || err?.privacy_accepted?.[0] || 'Registration failed. Please try again.');
      return false;
    } finally {
      setLoading(false);
    }
  };

  // The way out for someone whose store is not on the list. It used to be a disabled
  // button whose only job was to tell you that you could not press it.
  const skipStore = async () => {
    setSelectedStore(null);
    if (await submitRegistration()) {
      setStep('email_sent');
      requestAnimationFrame(() => window.scrollTo(0, 0));
    }
  };

  const handleNext = async () => {
    setError(null);

    switch (step) {
      case 'welcome':
        setStep('info');
        requestAnimationFrame(() => window.scrollTo(0, 0));
        break;

      case 'info':
        if (!formData.email || !formData.first_name || !formData.last_name) {
          setError('Please fill in all required fields');
          return;
        }
        // Validate email before continuing
        const validation = validateEmail(formData.email);
        setEmailValidation(validation);
        if (!validation.isValid) {
          if (validation.suggestion) {
            setError(`Did you mean ${validation.suggestion}? Please check your email address.`);
          } else {
            setError(validation.error || 'Please enter a valid email address');
          }
          return;
        }
        // Check terms accepted
        if (!termsAccepted) {
          setError('You must accept the Terms of Service and Privacy Policy to continue.');
          return;
        }
        setStep('location');
        requestAnimationFrame(() => window.scrollTo(0, 0));
        break;

      case 'location': {
        // Ask what this ZIP means before deciding what to show. A lookup failure falls
        // back to today's store picker rather than stranding the signup.
        setLoading(true);
        let tier: ZipTier | null = null;
        try {
          tier = await deliveryAPI.getZipTier(formData.zip_code || '');
        } catch (err: any) {
          // Busy is not "unknown ZIP": dropping a Caldwell customer into the Washington
          // store picker because the server was busy would be the wrong page, silently.
          if (isRateLimited(err)) {
            setLoading(false);
            setError(BUSY_MESSAGE);
            return;
          }
          tier = null;
        }
        setLoading(false);
        setZipTier(tier);

        if (tier?.status === 'available') {
          setStep('delivery');
        } else if (tier?.status === 'opening') {
          setStep('opening');
        } else {
          await loadStores();
          setStep('store');
        }
        requestAnimationFrame(() => window.scrollTo(0, 0));
        break;
      }

      case 'delivery': {
        if (!deliveryStreet.trim()) {
          setError('Please enter your street address.');
          return;
        }
        if (!(await submitRegistration())) return;
        // No account exists yet, so this address cannot become a subscription here: it is
        // recorded as a lead, and the dashboard card completes it in one tap once they
        // have verified their email and set a password.
        try {
          await deliveryAPI.recordDeliveryAddress({
            name: (formData.first_name + ' ' + formData.last_name).trim(),
            email: formData.email,
            phone: formData.phone,
            address_line1: deliveryStreet.trim(),
            city: formData.city,
            state: formData.state,
            zip_code: formData.zip_code,
            source: 'bridge_club_registration',
          });
        } catch {
          // Their account is made and the card will ask again. Never fail a signup here.
        }
        setStep('email_sent');
        requestAnimationFrame(() => window.scrollTo(0, 0));
        break;
      }

      case 'opening': {
        if (!(await submitRegistration())) return;
        if (wantsOpeningAlert) {
          try {
            await deliveryAPI.recordOpeningInterest({
              email: formData.email,
              zip_code: formData.zip_code,
              city: formData.city,
              state: formData.state,
              first_name: formData.first_name,
              last_name: formData.last_name,
              opt_in: true,
              source: 'bridge_club_registration',
            });
          } catch {
            // The member is captured server-side either way; only the consent is lost.
          }
        }
        setStep('email_sent');
        requestAnimationFrame(() => window.scrollTo(0, 0));
        break;
      }

      case 'store': {
        // Which ID field depends on the store type: legacy rows are Customers.
        const storeIds: Record<string, number> = selectedStore ? (
          selectedStore.is_legacy
            ? { linked_store_id: selectedStore.id }
            : { home_location_id: selectedStore.id }
        ) : {};
        if (await submitRegistration(storeIds)) {
          setStep('email_sent');
          requestAnimationFrame(() => window.scrollTo(0, 0));
        }
        break;
      }

      case 'email_sent':
        navigate('/bridge-club/login');
        break;
    }
  };

  const handleBack = () => {
    switch (step) {
      case 'info':
        setStep('welcome');
        break;
      case 'location':
        setStep('info');
        break;
      case 'delivery':
      case 'opening':
      case 'store':
        setStep('location');
        break;
    }
    requestAnimationFrame(() => window.scrollTo(0, 0));
  };

  const renderWelcome = () => (
    <div className="onboarding-welcome">
      <img src="/rbf-logo.png" alt="Red Bridge Farm Feed Co." className="welcome-logo" />
      <h1>Welcome to Bridge Club</h1>
      <p className="welcome-tagline">Smarter Feeding Starts Here.</p>

      <div className="benefits-list">
        <div className="benefit-item">
          <span className="benefit-icon">🎁</span>
          <div>
            <span className="benefit-title">Earn Rewards</span>
            <span className="benefit-desc">Buy 10 bags, get 11th FREE!</span>
          </div>
        </div>
        <div className="benefit-item">
          <span className="benefit-icon">🐾</span>
          <div>
            <span className="benefit-title">Track Your Animals</span>
            <span className="benefit-desc">Personalized feed recommendations</span>
          </div>
        </div>
        <div className="benefit-item">
          <span className="benefit-icon">🔔</span>
          <div>
            <span className="benefit-title">Never Run Out</span>
            <span className="benefit-desc">Feed reminders before you're empty</span>
          </div>
        </div>
        <div className="benefit-item">
          <span className="benefit-icon">🌤️</span>
          <div>
            <span className="benefit-title">Weather Alerts</span>
            <span className="benefit-desc">Tips for your specific animals</span>
          </div>
        </div>
        <div className="benefit-item">
          <span className="benefit-icon">🏆</span>
          <div>
            <span className="benefit-title">Farm Challenge</span>
            <span className="benefit-desc">Earn rewards & help improve feed</span>
          </div>
        </div>
      </div>

      <button onClick={handleNext} className="welcome-btn">
        Get Started
      </button>

      <p className="login-link">
        Already a member? <a href="/bridge-club/login">Sign In</a>
      </p>
    </div>
  );

  const renderInfo = () => (
    <div className="onboarding-form">
      <h2>Create Your Account</h2>

      {error && <div className="error-message">{error}</div>}

      <div className="form-row">
        <div className="form-group">
          <label>First Name *</label>
          <input
            type="text"
            value={formData.first_name}
            onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
            placeholder="First name"
          />
        </div>
        <div className="form-group">
          <label>Last Name *</label>
          <input
            type="text"
            value={formData.last_name}
            onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
            placeholder="Last name"
          />
        </div>
      </div>

      <div className="form-group">
        <label>Email *</label>
        <input
          type="email"
          value={formData.email}
          onChange={(e) => handleEmailChange(e.target.value)}
          onBlur={() => {
            setEmailTouched(true);
            setEmailValidation(validateEmail(formData.email));
          }}
          placeholder="your@email.com"
          className={emailTouched && !emailValidation.isValid ? 'input-error' : ''}
        />
        {emailTouched && !emailValidation.isValid && (
          <div className="email-validation">
            {emailValidation.suggestion ? (
              <div className="email-suggestion">
                <span className="suggestion-text">Did you mean <strong>{emailValidation.suggestion}</strong>?</span>
                <button
                  type="button"
                  onClick={acceptEmailSuggestion}
                  className="suggestion-btn"
                >
                  Yes, fix it
                </button>
              </div>
            ) : (
              <span className="validation-error">{emailValidation.error}</span>
            )}
          </div>
        )}
      </div>

      <div className="form-group">
        <label>Phone</label>
        <input
          type="tel"
          value={formData.phone}
          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
          placeholder="(555) 555-5555"
        />
      </div>

      <div className="form-group consent-group">
        <label className="consent-label">
          <input
            type="checkbox"
            checked={termsAccepted}
            onChange={(e) => setTermsAccepted(e.target.checked)}
            className="consent-checkbox"
          />
          <span className="consent-text">
            I agree to the{' '}
            <a href="/bridge-club/terms" target="_blank" rel="noopener noreferrer">
              Terms of Service
            </a>
            {' '}and{' '}
            <a href="/bridge-club/privacy" target="_blank" rel="noopener noreferrer">
              Privacy Policy
            </a>
            {' '}*
          </span>
        </label>
      </div>
    </div>
  );

  const renderLocation = () => (
    <div className="onboarding-form">
      <h2>Where Are You Located?</h2>
      <p className="form-subtitle">This helps us find stores near you and send weather alerts</p>

      {error && <div className="error-message">{error}</div>}

      <div className="form-group">
        <label>City</label>
        <input
          type="text"
          value={formData.city}
          onChange={(e) => setFormData({ ...formData, city: e.target.value })}
          placeholder="City"
        />
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>State</label>
          <select
            value={formData.state}
            onChange={(e) => setFormData({ ...formData, state: e.target.value })}
          >
            <option value="WA">Washington</option>
            <option value="ID">Idaho</option>
            <option value="OR">Oregon</option>
            <option value="MT">Montana</option>
          </select>
        </div>
        <div className="form-group">
          <label>ZIP Code</label>
          <input
            type="text"
            value={formData.zip_code}
            onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
            placeholder="ZIP"
          />
        </div>
      </div>
    </div>
  );

  // Heading, product line and paragraphs come from the server (delivery/opening.py), so
  // this flow and the dashboard card cannot say different things.
  const renderCopy = () => zipTier?.copy && (
    <>
      <h2>{zipTier.copy.heading}</h2>
      <p className="form-subtitle">{zipTier.copy.product}</p>
      {zipTier.copy.paragraphs.map((text, i) => (
        <p key={i} className="form-paragraph">{text}</p>
      ))}
      {/* The service switch (delivery/pause.py): signups stay open while delivery is
          paused - nobody is charged before activation - but the page says so. */}
      {zipTier.service_pause_line && (
        <p className="form-paragraph" style={{ color: '#991B1B', fontWeight: 600 }}>{zipTier.service_pause_line}</p>
      )}
    </>
  );

  // The wholesale way through, on every tier: a farm is a customer today, not a
  // waiting-list entry.
  const renderWholesale = () => zipTier?.copy && (
    <WholesaleLine wholesale={zipTier.copy.wholesale} />
  );

  // A delivery ZIP. There is no store to choose here, so we do not ask - we take the
  // address a driver will need. The copy says when delivery starts; being on the first
  // route is the reason to set up now.
  const renderDelivery = () => (
    <div className="onboarding-form">
      {renderCopy()}

      {error && <div className="error-message">{error}</div>}

      <div className="form-group">
        <label>Street address *</label>
        <input
          type="text"
          value={deliveryStreet}
          onChange={(e) => setDeliveryStreet(e.target.value)}
          placeholder="1234 Farm Rd"
          autoFocus
        />
        <p className="field-hint">
          {formData.city || 'Your town'}{formData.zip_code ? `, ${formData.zip_code}` : ''} —
          we need the street so the driver can find you. You will confirm it when you sign in,
          and nothing is charged until your first delivery.
        </p>
      </div>

      {renderWholesale()}
    </div>
  );

  // An opening-area ZIP. Every store on the list would be hundreds of miles away, so we
  // do not show it; we say what is coming and offer the alert.
  const renderOpening = () => (
    <div className="onboarding-form">
      {renderCopy()}

      {error && <div className="error-message">{error}</div>}

      <div className="form-group">
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={wantsOpeningAlert}
            onChange={(e) => setWantsOpeningAlert(e.target.checked)}
          />
          <span>Tell me when the Caldwell store opens.</span>
        </label>
      </div>

      {renderWholesale()}
    </div>
  );

  const renderStore = () => (
    <div className="onboarding-form">
      <h2>Choose Your Home Store</h2>
      <p className="form-subtitle">This is where you'll earn and redeem rewards</p>

      {error && <div className="error-message">{error}</div>}

      <div className="stores-list">
        {stores.length === 0 ? (
          <p className="no-stores">No stores found in your area. You can link a store later.</p>
        ) : (
          stores.map((store) => (
            <div
              key={store.id}
              className={`store-option ${selectedStore?.id === store.id ? 'selected' : ''}`}
              onClick={() => {
                setSelectedStore(store);
                // Clear loyalty ID when switching stores
                if (store.id !== selectedStore?.id) {
                  setFormData({ ...formData, partner_loyalty_id: '' });
                }
              }}
            >
              <div className="store-radio">
                {selectedStore?.id === store.id && <div className="radio-check" />}
              </div>
              <div className="store-info">
                {/* Store name from API - should show "NorthWest Seed & Pet - Sprague" etc */}
                <span className="store-name">{store.name}</span>
                <span className="store-location">{store.city}, {store.state}</span>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Optional loyalty ID field - only shown when a store is selected */}
      {selectedStore && (
        <div className="loyalty-id-section">
          <div className="form-group">
            <label>Do you have a {selectedStore.name} loyalty number? (optional)</label>
            <input
              type="text"
              value={formData.partner_loyalty_id}
              onChange={(e) => setFormData({ ...formData, partner_loyalty_id: e.target.value })}
              placeholder="Enter your loyalty ID"
            />
            <p className="field-hint">
              If you have an existing account at {selectedStore.name}, entering your loyalty number will link it to your Bridge Club account.
            </p>
          </div>
        </div>
      )}

      {isLocalSignup ? (
        <button
          onClick={() => {
            setSelectedStore(null);
            setFormData({ ...formData, partner_loyalty_id: '' });
          }}
          className={`skip-store ${!selectedStore ? 'active' : ''}`}
        >
          Skip — use Kettle Falls mill
        </button>
      ) : (
        <>
          <button
            type="button"
            onClick={skipStore}
            disabled={loading}
            className="skip-store"
          >
            My store isn't listed
          </button>
          <p className="field-hint">
            You can link a store later from your profile — it is only where you earn and
            redeem rewards.
          </p>
        </>
      )}

      {renderWholesale()}
    </div>
  );

  const renderEmailSent = () => (
    <div className="onboarding-complete">
      <div className="complete-icon">📧</div>
      <h2>Check Your Email</h2>
      <p>We've sent a verification link to:</p>
      <p className="email-highlight">{formData.email}</p>

      <div className="next-steps">
        <h3>Next Steps</h3>
        <div className="step-item">
          <span className="step-number">1</span>
          <span className="step-text">Check your email inbox (and spam folder)</span>
        </div>
        <div className="step-item">
          <span className="step-number">2</span>
          <span className="step-text">Click the verification link</span>
        </div>
        <div className="step-item">
          <span className="step-number">3</span>
          <span className="step-text">Create your password and start earning rewards!</span>
        </div>
      </div>

      <p className="email-note">
        The link expires in 24 hours. Didn't receive it? Check your spam folder or sign up again.
      </p>
    </div>
  );

  return (
    <div className="onboarding-page">
      {step !== 'welcome' && step !== 'email_sent' && (
        <div className="onboarding-header">
          <button onClick={handleBack} className="back-btn">← Back</button>
          <div className="progress-dots">
            <div className={`dot ${['info', 'location', 'delivery', 'opening', 'store'].includes(step) ? 'active' : ''}`} />
            <div className={`dot ${['location', 'delivery', 'opening', 'store'].includes(step) ? 'active' : ''}`} />
            <div className={`dot ${['delivery', 'opening', 'store'].includes(step) ? 'active' : ''}`} />
          </div>
          <div style={{ width: 60 }} />
        </div>
      )}

      <div className="onboarding-content">
        {step === 'welcome' && renderWelcome()}
        {step === 'info' && renderInfo()}
        {step === 'location' && renderLocation()}
        {step === 'delivery' && renderDelivery()}
        {step === 'opening' && renderOpening()}
        {step === 'store' && renderStore()}
        {step === 'email_sent' && renderEmailSent()}
      </div>

      {/* No footer on the check-your-email screen (Brad, 2026-09-23): "They don't
          have a password yet. So why take them to the login?" The next step is in
          their inbox, and the screen says so. */}
      {step !== 'welcome' && step !== 'email_sent' && (
        <div className="onboarding-footer">
          <button
            onClick={handleNext}
            disabled={loading || (step === 'store' && !isLocalSignup && !selectedStore)}
            className="next-btn"
          >
            {loading
              ? 'Sending verification email...'
              : step === 'delivery'
                  ? 'Set up my delivery'
                  : step === 'opening'
                    ? 'Create my account'
                    : 'Continue'}
          </button>
        </div>
      )}
    </div>
  );
};

export default OnboardingFlow;
